import {
  PartnerRegisterPostError,
  PartnerRegisterPostResponse,
} from '@/api/model/authentication/register.ts';
import { usePartnerRegisterPost } from '@/api/services/authentication/register.ts';
import { PartnerRegistrationFormType } from '@/components/Forms/PartnerRegistration/usePartnerRegistrationForm.ts';
import { useMessage } from '@/contexts/message/MessageContext.ts';
import { useTranslations } from '@/contexts/translation/useTranslations';
import useGetAuthentication from '@/domains/authentication/useGetAuthentication.ts';
import { AxiosError, AxiosResponse } from 'axios';
import { useState } from 'react';

/**
 * Relaie la soumission du formulaire d'inscription partenaire vers le monolithe.
 * Mappe les champs front (PartnerRegistrationFormType) vers les champs $_POST attendus
 * par apps/my/controllers/partners.php::_registerPartner(), en ajoutant sec_token + appId
 * (lus depuis authenticationData). Gère succès (redirection) et erreur (message).
 */
const usePartnerRegistration = () => {
  const { data } = useGetAuthentication();
  const { mutate, isPending } = usePartnerRegisterPost();
  const { successMessage, errorMessage } = useMessage();
  const { t } = useTranslations({ ns: 'window' });
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  const attributes = data?.pageData?.attributes;

  const onSubmit = (values: PartnerRegistrationFormType) => {
    mutate(
      {
        appId: attributes?.appId ?? '',
        sec_token: attributes?.sec_token ?? '',
        email: values.email,
        password: values.password,
        name: values.name,
        siren: values.siren ?? '',
        orias: values.orias ?? '',
        partner_code: values.partner_code ?? '',
        contact_firstname: values.contact_firstname,
        contact_lastname: values.contact_lastname,
        telephone: values.telephone,
        adresse: values.address,
        zipcode: values.zipcode,
        city: values.city,
        id_country: values.country,
        'how-know-us': values.referral_source,
        'cf-turnstile-response': values['cf-turnstile-response'] ?? '',
      },
      {
        onSuccess: (response: AxiosResponse<PartnerRegisterPostResponse>) => {
          if (response.data?.redirect_url) {
            document.location.href = response.data.redirect_url;
            return;
          }
          successMessage({ content: t('form.registration_success') });
        },
        onError: (error: AxiosError<PartnerRegisterPostError>) => {
          // Le token Turnstile est à usage unique : la modale restant ouverte pour
          // corriger l'erreur, il faut regénérer un challenge avant la prochaine tentative.
          setCaptchaResetKey((key) => key + 1);
          errorMessage({
            content:
              error?.response?.data?.error_message ??
              t('form.registration_error'),
          });
        },
      },
    );
  };

  return { onSubmit, loading: isPending, captchaResetKey };
};

export default usePartnerRegistration;
